import React, { useEffect, useState } from 'react';
import { 
  BrowserRouter as Router, 
  Route, 
  Switch, 
  Redirect, 
  useLocation,
  useHistory
} from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import HomePage from './pages/HomePage';
import TripDetailsPage from './pages/TripDetailsPage';
import POIPage from './pages/POIPage';
import MyTrips from './pages/MyTrips';
import FavoritesPage from './pages/FavoritesPage';
import NotFoundPage from './pages/NotFoundPage';
import PrivateRoute from './components/PrivateRoute';
import Footer from './components/Footer';
import theme from './theme';
import ProfilePage from './pages/ProfilePage';
import BookingHistoryPage from './pages/BookingHistoryPage';
import BookingDetailsPage from './pages/BookingDetailsPage';
import FAQ from './components/FAQ';
import { LanguageProvider } from './contexts/LanguageContext';
import CacheDebugPanel from './components/CacheDebugPanel';
import cacheInitializer from './services/cacheInitializer';
import SubscriptionPage from './pages/SubscriptionPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ToursPage from './pages/ToursPage';
import OperatorPage from './pages/OperatorPage';
import OperatorDashboard from './pages/OperatorDashboard';
import AdminDashboard from './pages/AdminDashboard';
import TourForm from './components/TourForm';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import tokenManager from './services/tokenManager';
import TourManagementPage from './pages/TourManagementPage';
import OperatorProfilePage from './pages/OperatorProfilePage';
import LegalDocumentPage from './pages/LegalDocumentPage';
import CookieBanner from './components/CookieBanner';
import AboutUsPage from './pages/AboutUsPage';
import OperatorPaymentHistory from './pages/OperatorPaymentHistory';


const ConditionalFooter = () => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const isTripDetails = location.pathname.startsWith('/trip/');
  const isOperator = location.pathname === '/operator';
  
  return !isHomePage && !isTripDetails && !isOperator && <Footer />;
};

const ProtectedRoute = ({ children, requiredRole, ...rest }) => {
  const history = useHistory();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Check if token actually needs refreshing before making API call
      if (!tokenManager.shouldRefreshToken()) {
        // Token is still valid, use cached user data
        const userData = localStorage.getItem('userData');
        if (userData) {
          try {
            const user = JSON.parse(userData);
            setIsAuthenticated(true);
            setUserRole(user.role);
            setIsLoading(false);
            console.log('🔑 ProtectedRoute: Using cached auth, skipping refresh');
            return;
          } catch (error) {
            console.error('Error parsing cached user data:', error);
          }
        }
      }

      // Token needs refresh or no cached data
      axios.post('/auth/refresh-token')
        .then(response => {
          setIsAuthenticated(true);
          setUserRole(response.data.user.role);
          localStorage.setItem('userData', JSON.stringify(response.data.user));
        })
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('userData');
          setIsAuthenticated(false);
          setUserRole(null);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, []);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Route
      {...rest}
      render={({ location }) => {
        if (!isAuthenticated) {
          return <Redirect to={{ pathname: "/login", state: { from: location } }} />;
        }

        if (requiredRole && userRole !== requiredRole) {
          // Redirect based on role
          if (userRole === 'operator') {
            return <Redirect to="/operator/dashboard" />;
          } else if (userRole === 'admin') {
            return <Redirect to="/admin" />;
          } else {
            return <Redirect to="/" />;
          }
        }

        return children;
      }}
    />
  );
};

const PublicRoute = ({ children, ...rest }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Check if token actually needs refreshing before making API call
      if (!tokenManager.shouldRefreshToken()) {
        // Token is still valid, use cached user data
        const userData = localStorage.getItem('userData');
        if (userData) {
          try {
            const user = JSON.parse(userData);
            setIsAuthenticated(true);
            setUserRole(user.role);
            setIsLoading(false);
            console.log('🔑 PublicRoute: Using cached auth, skipping refresh');
            return;
          } catch (error) {
            console.error('Error parsing cached user data:', error);
          }
        }
      }

      // Token needs refresh or no cached data
      axios.post('/auth/refresh-token')
        .then(response => {
          setIsAuthenticated(true);
          setUserRole(response.data.user.role);
          localStorage.setItem('userData', JSON.stringify(response.data.user));
        })
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('userData');
          setIsAuthenticated(false);
          setUserRole(null);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, []);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Route
      {...rest}
      render={({ location }) => {
        if (isAuthenticated) {
          // Redirect based on role
          if (userRole === 'operator') {
            return <Redirect to="/operator/dashboard" />;
          } else if (userRole === 'admin') {
            return <Redirect to="/admin" />;
          }
        }
        return children;
      }}
    />
  );
};

const AppContent = () => {
  const userRole = localStorage.getItem('userRole');
  const token = localStorage.getItem('token');

  // Function to check if user should be redirected
  const shouldRedirect = () => {
    if (!token) return false;
    if (userRole === 'operator') return true;
    if (userRole === 'admin') return true;
    return false;
  };

  // Function to get redirect path based on role
  const getRedirectPath = () => {
    if (userRole === 'operator') return '/operator/dashboard';
    if (userRole === 'admin') return '/admin';
    return '/';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <div style={{ flex: 1 }}>
        <Switch>
          {/* Public routes with role-based redirection */}
          <Route exact path="/" render={() => {
            return shouldRedirect() ? <Redirect to={getRedirectPath()} /> : <HomePage />;
          }} />
          
          <Route path="/login" render={() => {
            return shouldRedirect() ? <Redirect to={getRedirectPath()} /> : <LoginPage />;
          }} />
          
          <Route path="/signup" render={() => {
            return shouldRedirect() ? <Redirect to={getRedirectPath()} /> : <SignupPage />;
          }} />
          
          <Route path="/tours" render={() => {
            return shouldRedirect() ? <Redirect to={getRedirectPath()} /> : <ToursPage />;
          }} />

          <Route path="/operators/:id/profile" component={OperatorProfilePage} />

          <Route exact path="/operator" render={() => {
            return shouldRedirect() ? <Redirect to={getRedirectPath()} /> : <OperatorPage />;
          }} />

          {/* Protected operator routes */}
          <PrivateRoute 
            path="/operator/dashboard" 
            component={OperatorDashboard}
            requireRole="operator"
          />
          <PrivateRoute 
            path="/operator-payment-history" 
            component={OperatorPaymentHistory}
            requireRole="operator"
          />
          <PrivateRoute 
            path="/operator/tours/new" 
            component={TourForm}
            requireRole="operator"
          />
          <PrivateRoute 
            path="/operator/tours/:id/edit" 
            component={TourForm}
            requireRole="operator"
          />
          <PrivateRoute 
            path="/operator/tours/:tourId/manage" 
            component={TourManagementPage}
            requireRole="operator"
          />

          {/* Protected admin routes */}
          <PrivateRoute 
            path="/admin" 
            component={AdminDashboard}
            requireRole="admin"
          />

          {/* Regular user routes - redirect operators and admins */}
          <Route path="/subscription" render={() => {
            return shouldRedirect() ? <Redirect to={getRedirectPath()} /> : (
              <PrivateRoute component={SubscriptionPage} />
            );
          }} />

          <Route path="/poi/:id" render={() => {
            return shouldRedirect() ? <Redirect to={getRedirectPath()} /> : <POIPage />;
          }} />

          <Route path="/my-trips" render={() => {
            return shouldRedirect() ? <Redirect to={getRedirectPath()} /> : (
              <PrivateRoute component={MyTrips} />
            );
          }} />

          <Route path="/trip/:id" render={() => {
            return shouldRedirect() ? <Redirect to={getRedirectPath()} /> : (
              <PrivateRoute component={TripDetailsPage} />
            );
          }} />

          <Route path="/favorites" render={() => {
            return shouldRedirect() ? <Redirect to={getRedirectPath()} /> : (
              <PrivateRoute component={FavoritesPage} />
            );
          }} />

          <Route path="/profile" render={() => {
            return shouldRedirect() ? <Redirect to={getRedirectPath()} /> : (
              <PrivateRoute component={ProfilePage} />
            );
          }} />

          <Route path="/bookings" exact render={() => {
            return shouldRedirect() ? <Redirect to={getRedirectPath()} /> : (
              <PrivateRoute component={BookingHistoryPage} />
            );
          }} />

          <Route path="/booking/:id" render={() => {
            return shouldRedirect() ? <Redirect to={getRedirectPath()} /> : (
              <PrivateRoute component={BookingDetailsPage} />
            );
          }} />

          <Route path="/faq" render={() => {
            return shouldRedirect() ? <Redirect to={getRedirectPath()} /> : <FAQ />;
          }} />

          {/* Legal routes should be accessible without redirection */}
          <Route path="/legal/:document" component={LegalDocumentPage} />

          {/* About Us route should be accessible without redirection */}
          <Route path="/about" component={AboutUsPage} />

          <Route path="/operator-dashboard" element={<OperatorDashboard />} />
          <Route path="/operator-payment-history" element={<OperatorPaymentHistory />} />

          <Route component={NotFoundPage} />
        </Switch>
      </div>
      <ConditionalFooter />
      <ToastContainer />
      <CookieBanner />
      <CacheDebugPanel />
    </div>
  );
};

function App() {
  useEffect(() => {
    // Initialize essential cache data when app starts
    cacheInitializer.initializeEssentialData();
    
    // Initialize user data if user is already logged in
    const token = localStorage.getItem('token');
    if (token) {
      cacheInitializer.initializeUserData();
    }
  }, []);

  return (
    <LanguageProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorBoundary>
          <AuthProvider>
            <Router>
              <AppContent />
            </Router>
          </AuthProvider>
        </ErrorBoundary>
      </ThemeProvider>
    </LanguageProvider>
  );
}

export default App;
